import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Truck, X, MapPin, Phone, Clock, Package, 
  ChevronRight, AlertCircle, CheckCircle, Loader2, HelpCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../services/api';
import { MaxChatWidget } from './MaxChatWidget';

export default function DeliveryOrderModal({ isOpen, onClose, customer }) {
  const [step, setStep] = useState(1);
  const [zones, setZones] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    customerName: customer?.name || '',
    customerPhone: customer?.phone || '',
    streetNumber: '',
    streetName: '',
    city: '',
    postalCode: '',
    customerAddress: '',
    zone: '',
    items: '',
    totalAmount: '',
    slot: '',
    notes: ''
  });

  // Mapping villes -> zones
  const cityZoneMapping = {
    'vermelles': 'zone_proche',
    'noyelles-lès-vermelles': 'zone_proche',
    'noyelles les vermelles': 'zone_proche',
    'annequin': 'zone_proche',
    'beuvry': 'zone_moyenne',
    'labourse': 'zone_moyenne',
    'sailly-labourse': 'zone_moyenne',
    'sailly labourse': 'zone_moyenne',
    'cambrin': 'zone_moyenne',
    'béthune': 'zone_eloignee',
    'bethune': 'zone_eloignee',
    'lens': 'zone_eloignee',
    'liévin': 'zone_eloignee',
    'lievin': 'zone_eloignee',
  };

  // Détection automatique de la zone basée sur la ville
  const detectZoneFromCity = (cityName) => {
    const normalizedCity = cityName.toLowerCase().trim();
    return cityZoneMapping[normalizedCity] || 'zone_eloignee';
  };

  // Générer créneaux en temps réel (masquer les passés)
  const getAvailableSlots = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    const allSlots = [
      { time: '21:00-21:30', startHour: 21, startMinute: 0 },
      { time: '21:30-22:00', startHour: 21, startMinute: 30 },
      { time: '22:00-22:30', startHour: 22, startMinute: 0 },
      { time: '22:30-23:00', startHour: 22, startMinute: 30 },
      { time: '23:00-23:30', startHour: 23, startMinute: 0 },
      { time: '23:30-00:00', startHour: 23, startMinute: 30 },
      { time: '00:00-00:30', startHour: 0, startMinute: 0 },
      { time: '00:30-01:00', startHour: 0, startMinute: 30 },
      { time: '01:00-01:30', startHour: 1, startMinute: 0 },
      { time: '01:30-02:00', startHour: 1, startMinute: 30 },
    ];
    
    // Filtrer: garder seulement les créneaux futurs (+ 15 min de marge)
    return allSlots.filter(slot => {
      // Créneaux après minuit sont toujours dispo si on est avant minuit
      if (slot.startHour < 12 && currentHour >= 12) return true;
      // Sinon comparer normalement
      if (slot.startHour > currentHour) return true;
      if (slot.startHour === currentHour && slot.startMinute > currentMinute + 15) return true;
      return false;
    });
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
      setStep(1);
      setSuccess(false);
      setError('');
      setFormData(prev => ({
        ...prev,
        customerName: customer?.name || '',
        customerPhone: customer?.phone || ''
      }));
    }
  }, [isOpen, customer]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [zonesData, slotsData] = await Promise.all([
        api.getDeliveryZones(),
        api.getDeliverySlots()
      ]);
      const loadedZones = zonesData.zones || zonesData || [];
      setZones(loadedZones.length > 0 ? loadedZones : [
        { id: 'zone_proche', name: 'Zone Proche', cities: ['Vermelles', 'Noyelles-lès-Vermelles', 'Annequin'], minOrder: 20, deliveryFee: 0 },
        { id: 'zone_moyenne', name: 'Zone Moyenne', cities: ['Beuvry', 'Labourse', 'Sailly-Labourse', 'Cambrin'], minOrder: 30, deliveryFee: 0 },
        { id: 'zone_eloignee', name: 'Zone Éloignée', cities: ['Béthune', 'Lens', 'Liévin', 'Autres'], minOrder: 40, deliveryFee: 0 }
      ]);
      setSlots(slotsData.slots || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
      // Zones par défaut si erreur
      setZones([
        { id: 'zone_proche', name: 'Zone Proche', cities: ['Vermelles', 'Noyelles-lès-Vermelles', 'Annequin'], minOrder: 20, deliveryFee: 0 },
        { id: 'zone_moyenne', name: 'Zone Moyenne', cities: ['Beuvry', 'Labourse', 'Sailly-Labourse', 'Cambrin'], minOrder: 30, deliveryFee: 0 },
        { id: 'zone_eloignee', name: 'Zone Éloignée', cities: ['Béthune', 'Lens', 'Liévin', 'Autres'], minOrder: 40, deliveryFee: 0 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const selectedZone = zones.find(z => z.id === formData.zone);
  const minOrder = selectedZone?.minOrder || selectedZone?.minimumOrder || 40;

  // Construire l'adresse complète
  const buildFullAddress = () => {
    const parts = [];
    if (formData.streetNumber) parts.push(formData.streetNumber);
    if (formData.streetName) parts.push(formData.streetName);
    if (formData.postalCode || formData.city) {
      parts.push(`${formData.postalCode} ${formData.city}`.trim());
    }
    return parts.join(' ').trim() || formData.customerAddress;
  };

  const validateStep1 = () => {
    setError('');
    
    // Vérifier que la ville est renseignée (pour détection auto de zone)
    if (!formData.city && !formData.zone) {
      setError('Entrez votre ville pour détecter la zone');
      return false;
    }
    
    // Vérifier l'adresse
    if (!formData.streetName && !formData.customerAddress) {
      setError('Entrez le nom de votre rue');
      return false;
    }
    
    // Auto-sélectionner zone si pas fait
    if (!formData.zone && formData.city) {
      const detectedZone = detectZoneFromCity(formData.city);
      setFormData(prev => ({ ...prev, zone: detectedZone }));
    }
    
    return true;
  };

  const validateStep2 = () => {
    setError('');
    
    if (!formData.items.trim()) {
      setError('Décrivez ce que vous voulez commander');
      return false;
    }
    const amount = parseFloat(formData.totalAmount);
    if (isNaN(amount) || amount < minOrder) {
      setError(`Minimum de commande: ${minOrder}€`);
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.slot) {
      setError('Sélectionnez un créneau');
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
    else if (step === 3 && validateStep3()) handleSubmit();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const fullAddress = buildFullAddress();
      
      // Utiliser le bon endpoint
      await api.createDeliveryRequest({
        nom: formData.customerName.split(' ').slice(1).join(' ') || formData.customerName,
        prenom: formData.customerName.split(' ')[0],
        telephone: formData.customerPhone,
        adresse: fullAddress,
        zone: formData.zone,
        zoneLabel: selectedZone?.name || '',
        minimumCommande: minOrder,
        commentaire: `🛒 ${formData.items}\n\n📊 Montant estimé: ${formData.totalAmount}€\n🕐 Créneau: ${formData.slot}${formData.notes ? '\n📝 Notes: ' + formData.notes : ''}`,
        modePaiement: 'especes',
        type: 'delivery'
      });
      setSuccess(true);
    } catch (error) {
      console.error('Erreur commande:', error);
      setError(error.response?.data?.detail || 'Erreur lors de la commande. Appelez le 07 80 64 89 76');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl"
          style={{
            background: 'linear-gradient(135deg, #0a0015 0%, #1a0033 100%)',
            border: '2px solid #00FFFF',
            boxShadow: '0 0 40px rgba(0, 255, 255, 0.4)'
          }}
        >
          {/* Header */}
          <div className="sticky top-0 p-4 flex items-center justify-between border-b border-white/10 bg-black/50 backdrop-blur-xl rounded-t-3xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Me faire livrer</h2>
                <p className="text-gray-400 text-xs">Vendredi & Samedi • 21h-2h</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>

          {/* Success */}
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-8 text-center"
            >
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Commande envoyée !</h3>
              <p className="text-gray-400 mb-6">
                Nous avons bien reçu votre demande. Un livreur vous contactera bientôt pour confirmer.
              </p>
              <Button
                onClick={onClose}
                className="bg-gradient-to-r from-cyan-500 to-blue-500"
              >
                Fermer
              </Button>
            </motion.div>
          ) : loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-10 h-10 mx-auto text-cyan-400 animate-spin" />
              <p className="text-gray-400 mt-4">Chargement...</p>
            </div>
          ) : (
            <>
              {/* Progress */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  {[1, 2, 3].map((s) => (
                    <div
                      key={s}
                      className={`flex-1 h-1 mx-1 rounded-full transition-all ${
                        s <= step ? 'bg-cyan-500' : 'bg-white/20'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-gray-400 text-xs text-center">
                  Étape {step}/3 - {step === 1 ? 'Adresse' : step === 2 ? 'Commande' : 'Créneau'}
                </p>
              </div>

              {/* Content */}
              <div className="p-4">
                {/* Step 1: Zone & Address */}
                {step === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="text-white text-sm font-semibold mb-2 block">
                        🏠 Votre adresse de livraison
                      </label>
                      
                      {/* Numéro + Rue */}
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={formData.streetNumber}
                          onChange={(e) => setFormData({ ...formData, streetNumber: e.target.value })}
                          placeholder="N°"
                          className="w-16 p-3 rounded-xl bg-white/10 text-white border border-white/20 placeholder-gray-500 text-center"
                        />
                        <input
                          type="text"
                          value={formData.streetName}
                          onChange={(e) => setFormData({ ...formData, streetName: e.target.value })}
                          placeholder="Nom de la rue"
                          className="flex-1 p-3 rounded-xl bg-white/10 text-white border border-white/20 placeholder-gray-500"
                        />
                      </div>
                      
                      {/* Code postal + Ville */}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.postalCode}
                          onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                          placeholder="62980"
                          className="w-24 p-3 rounded-xl bg-white/10 text-white border border-white/20 placeholder-gray-500 text-center"
                        />
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => {
                            const city = e.target.value;
                            const detectedZone = detectZoneFromCity(city);
                            setFormData({ 
                              ...formData, 
                              city,
                              zone: detectedZone
                            });
                          }}
                          placeholder="Ville (ex: Vermelles)"
                          className="flex-1 p-3 rounded-xl bg-white/10 text-white border border-white/20 placeholder-gray-500"
                        />
                      </div>
                    </div>

                    {/* Zone détectée automatiquement */}
                    {formData.city && (
                      <div className={`p-3 rounded-xl ${selectedZone ? 'bg-cyan-500/20 border border-cyan-500/30' : 'bg-orange-500/20 border border-orange-500/30'}`}>
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-white font-semibold text-sm">
                              {selectedZone?.name || 'Zone Éloignée'}
                            </p>
                            <p className="text-gray-400 text-xs">Zone détectée automatiquement</p>
                          </div>
                          <div className="text-right">
                            <p className="text-cyan-400 font-bold">{minOrder}€ min</p>
                            <p className="text-green-400 text-xs">GRATUIT 🎉</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Sélection manuelle si besoin */}
                    {!formData.city && (
                      <div>
                        <label className="text-white text-sm font-semibold mb-2 block">
                          📍 Ou sélectionnez votre zone
                        </label>
                        <div className="space-y-2">
                          {zones.map((zone) => (
                            <button
                              key={zone.id}
                              onClick={() => setFormData({ ...formData, zone: zone.id })}
                              className={`w-full p-3 rounded-xl text-left transition-all ${
                                formData.zone === zone.id
                                  ? 'ring-2 ring-cyan-500 bg-cyan-500/20'
                                  : 'bg-white/5 hover:bg-white/10'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="text-white font-semibold">{zone.name}</p>
                                  <p className="text-gray-400 text-xs">{zone.cities?.join(', ')}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-cyan-400 font-bold">{zone.minOrder || zone.minimumOrder}€ min</p>
                                  <p className="text-green-400 text-xs">GRATUIT 🎉</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notes de livraison */}
                    <div>
                      <label className="text-white text-sm font-semibold mb-2 block">
                        📝 Instructions de livraison (optionnel)
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Ex: Code d'entrée, étage, digicode..."
                        className="w-full p-3 rounded-xl bg-white/10 text-white border border-white/20 resize-none h-16 placeholder-gray-500"
                      />
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Items & Amount */}
                {step === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="text-white text-sm font-semibold mb-2 block">
                        🛒 Que souhaitez-vous commander ?
                      </label>
                      <textarea
                        value={formData.items}
                        onChange={(e) => setFormData({ ...formData, items: e.target.value })}
                        placeholder="Ex: 1 pack Heineken, 2 bouteilles de rosé, 1 paquet de chips..."
                        className="w-full p-3 rounded-xl bg-white/10 text-white border border-white/20 resize-none h-32 placeholder-gray-500"
                      />
                    </div>

                    <div>
                      <label className="text-white text-sm font-semibold mb-2 block">
                        📊 Montant estimé de la commande
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          value={formData.totalAmount}
                          onChange={(e) => setFormData({ ...formData, totalAmount: e.target.value })}
                          placeholder={`Minimum ${minOrder}€`}
                          className="w-full p-3 pr-10 rounded-xl bg-white/10 text-white border border-white/20 placeholder-gray-500"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                      </div>
                      {selectedZone && (
                        <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 mt-2">
                          <p className="text-cyan-400 text-sm">
                            ✓ {selectedZone.name} : Minimum {minOrder}€ • Livraison GRATUITE 🎉
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Slot - Créneaux en temps réel */}
                {step === 3 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="text-white text-sm font-semibold mb-2 block">
                        🕐 Choisissez votre créneau
                      </label>
                      <p className="text-gray-400 text-xs mb-3">
                        Seuls les créneaux disponibles sont affichés
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {getAvailableSlots().map((slot) => (
                          <button
                            key={slot.time}
                            onClick={() => setFormData({ ...formData, slot: slot.time })}
                            className={`p-3 rounded-xl text-center transition-all ${
                              formData.slot === slot.time
                                ? 'ring-2 ring-cyan-500 bg-cyan-500/20'
                                : 'bg-white/5 hover:bg-white/10'
                            }`}
                          >
                            <p className={`font-semibold ${
                              formData.slot === slot.time ? 'text-cyan-400' : 'text-white'
                            }`}>
                              {slot.time}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Récap */}
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <h4 className="text-white font-semibold mb-3">📋 Récapitulatif</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Zone</span>
                          <span className="text-white">{selectedZone?.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Adresse</span>
                          <span className="text-white truncate max-w-[200px]">{buildFullAddress()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Montant</span>
                          <span className="text-cyan-400 font-bold">{formData.totalAmount}€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Livraison</span>
                          <span className="text-green-400 font-bold">GRATUITE 🎉</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Error */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-4 p-3 rounded-xl bg-red-500/20 border border-red-500/50 flex items-center gap-2"
                  >
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </motion.div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/10 flex gap-3">
                {step > 1 && (
                  <Button
                    onClick={() => setStep(step - 1)}
                    variant="outline"
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    Retour
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : step === 3 ? (
                    'Envoyer la commande'
                  ) : (
                    <>
                      Suivant
                      <ChevronRight className="w-5 h-5 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </motion.div>
        
        {/* 📦 Max - Assistant Livraison */}
        <MaxChatWidget 
          customerId={customer?.id}
          position="bottom-left"
          isDarkMode={true}
          compact={true}
        />
      </motion.div>
    </AnimatePresence>
  );
}
